import { useState, useEffect, useCallback } from 'react';
import { Folder, FolderOpen, FileText, Plus, Trash2, ArrowLeft, Sparkles, Loader2, Download, Star, Copy, Search } from 'lucide-react';
import { api } from '../lib/api';
import { printWorksheet } from '../lib/exportPdf';
import { presetById, fontById } from '../lib/worksheetStyles';

interface Project {
  id: string;
  name: string;
  color: string;
  createdAt: string;
  updatedAt: string;
}

interface Worksheet {
  id: string;
  title: string;
  settings: { subject?: string; grade?: string; pageSize?: string; orientation?: string };
  isFavorite?: boolean;
  createdAt: string;
  updatedAt: string;
}

type SortKey = 'updated' | 'title' | 'favorite';

const colorHexes = ['#2238B5', '#E25C45', '#5E8C68', '#E8B445', '#8568C2'];
const folderColors = [
  { bg: 'bg-cobalt-soft', border: 'border-cobalt', text: 'text-cobalt' },
  { bg: 'bg-coral-soft', border: 'border-coral', text: 'text-coral' },
  { bg: 'bg-sage-soft', border: 'border-sage', text: 'text-sage' },
  { bg: 'bg-sun-soft', border: 'border-sun', text: 'text-sun' },
  { bg: 'bg-lilac-soft', border: 'border-lilac', text: 'text-lilac' },
];

function colorToIndex(hex: string): number {
  const idx = colorHexes.findIndex((h) => h.toLowerCase() === hex?.toLowerCase());
  return idx >= 0 ? idx : 0;
}

interface ProjectsViewProps {
  onOpenEditor?: (data: {
    id: string;
    title: string;
    content?: string;
    folderId: string;
    subject: string;
    grade: string;
  }) => void;
}

export default function ProjectsView({ onOpenEditor }: ProjectsViewProps) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [defaultFolderId, setDefaultFolderId] = useState<string | null>(null);
  const [worksheets, setWorksheets] = useState<Worksheet[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingWorksheets, setIsLoadingWorksheets] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddProject, setShowAddProject] = useState(false);
  const [showAddWorksheet, setShowAddWorksheet] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', color: 0 });
  const [newWorksheet, setNewWorksheet] = useState({ title: '', subject: '', grade: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('updated');
  const [busyId, setBusyId] = useState<string | null>(null);

  const toggleFavorite = async (ws: Worksheet, e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !ws.isFavorite;
    setWorksheets((prev) => prev.map((w) => (w.id === ws.id ? { ...w, isFavorite: next } : w)));
    try {
      await api.worksheets.update(ws.id, { isFavorite: next });
    } catch (err: any) {
      setWorksheets((prev) => prev.map((w) => (w.id === ws.id ? { ...w, isFavorite: !next } : w)));
      setError(err.message);
    }
  };

  const handleDuplicate = async (ws: Worksheet, e: React.MouseEvent) => {
    e.stopPropagation();
    if (busyId || !defaultFolderId) return;
    try {
      setBusyId(ws.id);
      const full = await api.worksheets.get(ws.id);
      await api.worksheets.create({
        folderId: defaultFolderId,
        title: `${ws.title} (Kopie)`,
        content: full.content || {},
        settings: full.settings || {},
      });
      if (selectedProject) await loadWorksheets(selectedProject);
    } catch (err: any) {
      setError('Duplizieren fehlgeschlagen: ' + err.message);
    } finally {
      setBusyId(null);
    }
  };

  const visibleWorksheets = (() => {
    const q = search.trim().toLowerCase();
    let list = worksheets;
    if (q) {
      list = list.filter(
        (w) => w.title.toLowerCase().includes(q) || (w.settings?.subject || '').toLowerCase().includes(q),
      );
    }
    const sorted = [...list];
    if (sortKey === 'title') sorted.sort((a, b) => a.title.localeCompare(b.title, 'de'));
    else if (sortKey === 'favorite') sorted.sort((a, b) => Number(b.isFavorite) - Number(a.isFavorite) || (a.title.localeCompare(b.title, 'de')));
    else sorted.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    return sorted;
  })();

  const handleExportPdf = async (ws: Worksheet, e: React.MouseEvent) => {
    e.stopPropagation();
    if (exportingId) return;
    try {
      setExportingId(ws.id);
      const full = await api.worksheets.get(ws.id);
      const content = full.content || {};
      const html = typeof content === 'string' ? content : (content.html ?? '');
      const meta = (typeof content === 'object' && content.meta) || {};
      const preset = presetById(meta.gradeLevel || 'kl34');
      await printWorksheet(html, {
        title: ws.title,
        subject: meta.subject ?? full.settings?.subject,
        grade: meta.grade ?? full.settings?.grade,
        schoolYear: meta.schoolYear,
        teacher: meta.teacher,
        font: preset.font,
        lh: preset.lh,
        frame: meta.frame,
        family: fontById(meta.fontFamily || 'standard').stack,
      });
    } catch (err: any) {
      setError('PDF-Export fehlgeschlagen: ' + err.message);
    } finally {
      setExportingId(null);
    }
  };

  const loadProjects = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.projects.list();
      setProjects(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const loadWorksheets = useCallback(async (project: Project) => {
    try {
      setIsLoadingWorksheets(true);
      setError(null);
      let folders = await api.folders.list(project.id);
      if (folders.length === 0) {
        const created = await api.folders.create(project.id, 'Standard');
        folders = [created];
      }
      const folderId = folders[0].id;
      setDefaultFolderId(folderId);
      const sheets = await api.worksheets.list(folderId);
      setWorksheets(sheets);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoadingWorksheets(false);
    }
  }, []);

  const handleOpenProject = (project: Project) => {
    setSelectedProject(project);
    loadWorksheets(project);
  };

  const handleBackToProjects = () => {
    setSelectedProject(null);
    setDefaultFolderId(null);
    setWorksheets([]);
    setShowAddWorksheet(false);
  };

  const handleAddProject = async () => {
    if (!newProject.name.trim() || isSaving) return;
    try {
      setIsSaving(true);
      await api.projects.create({ name: newProject.name, color: colorHexes[newProject.color] });
      setNewProject({ name: '', color: 0 });
      setShowAddProject(false);
      await loadProjects();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteProject = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.projects.delete(id);
      setProjects((prev) => prev.filter((p) => p.id !== id));
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAddWorksheet = async () => {
    if (!newWorksheet.title.trim() || !defaultFolderId || isSaving) return;
    try {
      setIsSaving(true);
      await api.worksheets.create({
        folderId: defaultFolderId,
        title: newWorksheet.title,
        content: {},
        settings: {
          pageSize: 'A4',
          orientation: 'portrait',
          subject: newWorksheet.subject || 'Allgemein',
          grade: newWorksheet.grade || '',
        },
      });
      setNewWorksheet({ title: '', subject: '', grade: '' });
      setShowAddWorksheet(false);
      if (selectedProject) await loadWorksheets(selectedProject);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteWorksheet = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.worksheets.delete(id);
      setWorksheets((prev) => prev.filter((w) => w.id !== id));
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleOpenEditor = (ws: Worksheet) => {
    if (!onOpenEditor || !defaultFolderId) return;
    onOpenEditor({
      id: ws.id,
      title: ws.title,
      content: '',
      folderId: defaultFolderId,
      subject: ws.settings?.subject || '',
      grade: ws.settings?.grade || '',
    });
  };

  const handleCreateWorksheet = () => {
    if (!onOpenEditor || !defaultFolderId) return;
    onOpenEditor({
      id: '',
      title: '',
      content: '',
      folderId: defaultFolderId,
      subject: '',
      grade: '',
    });
  };

  // ==================== LOADING ====================
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-cobalt animate-spin" />
      </div>
    );
  }

  // ==================== ERROR ====================
  if (error && !selectedProject && projects.length === 0) {
    return (
      <div className="max-w-5xl mx-auto flex flex-col items-center justify-center min-h-[60vh] text-center">
        <p className="text-coral mb-4">{error}</p>
        <button onClick={loadProjects} className="sl-button sl-button-primary">
          Erneut versuchen
        </button>
      </div>
    );
  }

  // ==================== WORKSHEET LIST (inside a project) ====================
  if (selectedProject) {
    const colorIdx = colorToIndex(selectedProject.color);
    const colors = folderColors[colorIdx];
    return (
      <div className="max-w-5xl mx-auto">
        {error && (
          <div className="sl-card p-3 mb-4 border-coral bg-coral-soft text-coral text-sm">{error}</div>
        )}

        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={handleBackToProjects}
            className="p-2 rounded-xl hover:bg-paper-deep transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-ink" />
          </button>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center`}>
              <FolderOpen className="w-5 h-5 text-ink" />
            </div>
            <div>
              <h2 className="sl-h2">{selectedProject.name}</h2>
              <p className="text-sm text-ink-mute">{worksheets.length} Arbeitsblätter</p>
            </div>
          </div>
          <div className="ml-auto">
            <button
              onClick={() => setShowAddWorksheet(true)}
              className="sl-button sl-button-primary gap-2"
            >
              <Plus className="w-4 h-4" />
              Neues Arbeitsblatt
            </button>
          </div>
        </div>

        {showAddWorksheet && (
          <div className="sl-card p-5 mb-6">
            <h3 className="sl-h3 mb-4">Neues Arbeitsblatt</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <input
                type="text"
                placeholder="Titel *"
                value={newWorksheet.title}
                onChange={(e) => setNewWorksheet({ ...newWorksheet, title: e.target.value })}
                className="sl-input"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleAddWorksheet()}
              />
              <input
                type="text"
                placeholder="Fach"
                value={newWorksheet.subject}
                onChange={(e) => setNewWorksheet({ ...newWorksheet, subject: e.target.value })}
                className="sl-input"
              />
              <select
                value={newWorksheet.grade}
                onChange={(e) => setNewWorksheet({ ...newWorksheet, grade: e.target.value })}
                className="sl-input"
              >
                <option value="">Klasse wählen...</option>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].map((g) => (
                  <option key={g} value={String(g)}>
                    Klasse {g}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowAddWorksheet(false);
                  setNewWorksheet({ title: '', subject: '', grade: '' });
                }}
                className="sl-button sl-button-outline"
              >
                Abbrechen
              </button>
              <button
                onClick={handleAddWorksheet}
                disabled={!newWorksheet.title.trim() || isSaving}
                className="sl-button sl-button-primary disabled:opacity-50 gap-2"
              >
                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                Erstellen
              </button>
            </div>
          </div>
        )}

        {worksheets.length > 0 && !isLoadingWorksheets && (
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="w-4 h-4 text-ink-mute absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Arbeitsblätter durchsuchen…" className="sl-input !h-10 !pl-9" />
            </div>
            <select value={sortKey} onChange={(e) => setSortKey(e.target.value as SortKey)} className="sl-input !h-10 !w-auto">
              <option value="updated">Zuletzt geändert</option>
              <option value="title">Titel A–Z</option>
              <option value="favorite">Favoriten zuerst</option>
            </select>
          </div>
        )}

        {isLoadingWorksheets ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-cobalt animate-spin" />
          </div>
        ) : worksheets.length === 0 && !showAddWorksheet ? (
          <div className="sl-card p-8 text-center">
            <FileText className="w-12 h-12 text-ink-faint mx-auto mb-4" />
            <h3 className="sl-h3 mb-2 text-ink-soft">Noch keine Arbeitsblätter</h3>
            <p className="text-ink-mute mb-4">Erstelle dein erstes Arbeitsblatt.</p>
            <button
              onClick={handleCreateWorksheet}
              className="sl-button sl-button-primary gap-2"
            >
              <Plus className="w-5 h-5" />
              Arbeitsblatt erstellen
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {visibleWorksheets.length === 0 ? (
              <div className="sl-card p-6 text-center text-ink-mute">Keine Treffer für „{search}".</div>
            ) : visibleWorksheets.map((ws) => (
              <button
                key={ws.id}
                onClick={() => handleOpenEditor(ws)}
                className="sl-card p-4 flex items-center gap-4 group w-full text-left hover:shadow-elevation-1 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-paper-deep flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-ink-mute" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-ink truncate">{ws.title}</h4>
                  <div className="flex items-center gap-3 text-sm text-ink-mute mt-1">
                    {ws.settings?.subject && <span>{ws.settings.subject}</span>}
                    {ws.settings?.subject && ws.settings?.grade && (
                      <span className="w-1 h-1 rounded-full bg-ink-faint" />
                    )}
                    {ws.settings?.grade && <span>Klasse {ws.settings.grade}</span>}
                    {(ws.settings?.subject || ws.settings?.grade) && (
                      <span className="w-1 h-1 rounded-full bg-ink-faint" />
                    )}
                    <span>{new Date(ws.updatedAt).toLocaleDateString('de-DE')}</span>
                  </div>
                </div>
                {/* Favoritenstern – immer sichtbar */}
                <button
                  onClick={(e) => toggleFavorite(ws, e)}
                  className={`p-2 rounded-lg flex-shrink-0 transition-colors ${ws.isFavorite ? 'text-sun-deep' : 'text-ink-faint hover:text-sun-deep opacity-0 group-hover:opacity-100'}`}
                  title={ws.isFavorite ? 'Favorit entfernen' : 'Als Favorit markieren'}
                >
                  <Star className="w-4 h-4" fill={ws.isFavorite ? 'currentColor' : 'none'} />
                </button>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <button
                    onClick={(e) => handleDuplicate(ws, e)}
                    className="p-2 rounded-lg hover:bg-paper-deep text-ink-mute hover:text-ink"
                    title="Duplizieren"
                  >
                    {busyId === ws.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={(e) => handleExportPdf(ws, e)}
                    className="p-2 rounded-lg hover:bg-cobalt-soft text-ink-mute hover:text-cobalt"
                    title="Als PDF exportieren"
                  >
                    {exportingId === ws.id
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Download className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={(e) => handleDeleteWorksheet(ws.id, e)}
                    className="p-2 rounded-lg hover:bg-coral-soft text-ink-mute hover:text-coral"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </button>
            ))}

            <button
              onClick={handleCreateWorksheet}
              className="sl-card p-4 border-dashed border-2 border-rule bg-transparent hover:bg-paper-deep transition-colors flex items-center justify-center gap-2 text-ink-mute w-full"
            >
              <Plus className="w-5 h-5" />
              <span className="font-medium">Neues Arbeitsblatt</span>
            </button>
          </div>
        )}
      </div>
    );
  }

  // ==================== EMPTY STATE ====================
  if (projects.length === 0 && !showAddProject) {
    return (
      <div className="max-w-5xl mx-auto flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="w-20 h-20 rounded-2xl bg-cobalt-soft flex items-center justify-center mb-6">
          <Sparkles className="w-10 h-10 text-cobalt" />
        </div>
        <h2 className="sl-h1 mb-3">Willkommen bei SketchLessons!</h2>
        <p className="text-ink-soft max-w-md mb-8 leading-relaxed">
          Erstelle deinen ersten Ordner, um Arbeitsblätter zu organisieren.
        </p>
        <button
          onClick={() => setShowAddProject(true)}
          className="sl-button sl-button-primary gap-2"
        >
          <Plus className="w-5 h-5" />
          Ersten Ordner erstellen
        </button>
      </div>
    );
  }

  // ==================== PROJECT GRID ====================
  return (
    <div className="max-w-5xl mx-auto">
      {error && (
        <div className="sl-card p-3 mb-4 border-coral bg-coral-soft text-coral text-sm">{error}</div>
      )}

      {showAddProject && (
        <div className="sl-card p-6 mb-6">
          <h3 className="sl-h3 mb-4">Neuer Ordner</h3>
          <div className="space-y-4">
            <input
              type="text"
              placeholder="Name des Ordners (z.B. Mathematik)"
              value={newProject.name}
              onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
              className="sl-input"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleAddProject()}
            />
            <div className="flex items-center gap-2">
              <span className="text-sm text-ink-soft">Farbe:</span>
              {folderColors.map((color, i) => (
                <button
                  key={i}
                  onClick={() => setNewProject({ ...newProject, color: i })}
                  className={`w-8 h-8 rounded-lg ${color.bg} border-2 transition-all ${
                    newProject.color === i
                      ? color.border + ' ring-2 ring-cobalt ring-offset-2'
                      : 'border-transparent'
                  }`}
                />
              ))}
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowAddProject(false);
                  setNewProject({ name: '', color: 0 });
                }}
                className="sl-button sl-button-outline"
              >
                Abbrechen
              </button>
              <button
                onClick={handleAddProject}
                disabled={!newProject.name.trim() || isSaving}
                className="sl-button sl-button-primary disabled:opacity-50 gap-2"
              >
                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                Erstellen
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map((project) => {
          const colorIdx = colorToIndex(project.color);
          const colors = folderColors[colorIdx];
          return (
            <button
              key={project.id}
              onClick={() => handleOpenProject(project)}
              className="sl-card p-5 text-left group hover:shadow-elevation-2 transition-all relative"
            >
              <div className="flex items-start justify-between mb-3">
                <div
                  className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center transition-transform group-hover:scale-105`}
                >
                  <Folder className="w-6 h-6 text-ink" />
                </div>
                <button
                  onClick={(e) => handleDeleteProject(project.id, e)}
                  className="p-1 rounded-lg hover:bg-coral-soft text-ink-mute hover:text-coral opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <h3 className="font-semibold text-ink mb-1">{project.name}</h3>
              <p className="text-sm text-ink-mute">
                {new Date(project.updatedAt).toLocaleDateString('de-DE')}
              </p>
            </button>
          );
        })}

        <button
          onClick={() => setShowAddProject(true)}
          className="sl-card p-5 border-dashed border-2 border-rule bg-transparent hover:bg-paper-deep transition-colors flex flex-col items-center justify-center gap-3 text-ink-mute min-h-[140px]"
        >
          <Plus className="w-8 h-8" />
          <span className="font-medium">Neuer Ordner</span>
        </button>
      </div>
    </div>
  );
}
